import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useBlueMonk } from '@/contexts/BlueMonkContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BlueMonkPinEntry = () => {
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const { verifyPin, isClientAuthenticated, isClientPinVerified } = useBlueMonk();

  useEffect(() => {
    // Small delay to allow context to initialize from localStorage
    const timer = setTimeout(() => {
      // If already fully authenticated (PIN + identity), redirect to portal
      if (isClientAuthenticated()) {
        navigate('/bluemonk/portal');
      } else if (isClientPinVerified()) {
        // PIN verified but no identity - go to portal to select identity
        navigate('/bluemonk/portal');
      }
      setChecking(false);
    }, 100);
    return () => clearTimeout(timer);
  }, [isClientAuthenticated, isClientPinVerified, navigate]);

  // Show loading while checking auth state
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  const handleChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newPin = [...pin];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newPin[index + i] = digit;
        }
      });
      setPin(newPin);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    if (!/^\d*$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    // Move to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pinString = pin.join('');
    
    if (pinString.length !== 6) {
      toast.error('Please enter a 6-digit PIN');
      return;
    }

    setLoading(true);
    try {
      await verifyPin(pinString);
      toast.success('Access granted');
      navigate('/bluemonk/portal');
    } catch (error) {
      console.error('PIN verification error:', error);
      toast.error('Invalid PIN. Please try again.');
      setPin(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="w-full max-w-md p-8 bg-white shadow-xl">
          <div className="text-center mb-8">
            <div className="bg-white rounded-xl p-4 mx-auto mb-6 inline-block shadow-sm border">
              <img 
                src="/logo.png" 
                alt="Content Vista" 
                className="h-12 w-auto"
              />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">BlueMonk Ventures</h1>
            <p className="text-slate-500">Client Portal</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <p className="text-sm font-medium text-slate-700 text-center mb-4">
                Enter your access code
              </p>
              <div className="flex justify-center gap-2">
                {pin.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-semibold bg-slate-50 border-slate-200 focus:border-primary focus:ring-primary"
                    data-testid={`pin-input-${index}`}
                    autoFocus={index === 0}
                  />
                ))}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 h-12 text-base font-semibold"
              disabled={loading || pin.some(d => !d)}
              data-testid="pin-submit-btn"
            >
              {loading ? 'Verifying...' : 'Access Portal'}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t text-center">
            <p className="text-xs text-slate-400">
              Contact your Content Vista account manager if you need assistance
            </p>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default BlueMonkPinEntry;
